import { AlertTriangle, Lock } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import { useOrgContext } from "../../../context/OrgContext";
import { useAuth } from "../../../context/AuthContext";
import { OrganizationSSOPanel } from "./OrganizationSSOPanel";

export function OrganizationSSOPage() {
  const { t } = useLocale();
  const { user } = useAuth();
  const { currentOrgId, currentOrg } = useOrgContext();

  const isFeatureFlagOn = import.meta.env.VITE_FEATURE_SSO_OIDC === "true";

  // 1. Feature flag off → unavailable panel.
  if (!isFeatureFlagOn) {
    return (
      <PageShell title={t("sso.page.title")}>
        <EmptyState
          icon={<Lock className="h-5 w-5" />}
          title={t("sso.empty.featureOffTitle")}
          body={t("sso.empty.featureOffBody")}
        />
      </PageShell>
    );
  }

  // 2. Role gate (UI is also wrapped in <AdminRoute>; this is belt-and-suspenders).
  if (user && user.role !== "ADMIN" && user.role !== "ORG_ADMIN") {
    return (
      <PageShell title={t("sso.page.title")}>
        <EmptyState
          icon={<Lock className="h-5 w-5" />}
          title={t("sso.empty.notAdminTitle")}
          body={t("sso.empty.notAdminBody")}
        />
      </PageShell>
    );
  }

  // 3. No selected org.
  if (!currentOrgId) {
    return (
      <PageShell title={t("sso.page.title")}>
        <EmptyState
          icon={<AlertTriangle className="h-5 w-5" />}
          title={t("common.selectOrg")}
          body={t("common.selectOrgHint")}
        />
      </PageShell>
    );
  }

  return (
    <PageShell
      title={t("sso.page.title")}
      description={t("sso.page.description", {
        org: currentOrg?.name ?? "",
      })}
    >
      <OrganizationSSOPanel organizationId={currentOrgId} />
    </PageShell>
  );
}

// ── Internal building blocks ───────────────────────────────────────────────

function PageShell({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-6 sm:px-6">
      <header>
        <h1 className="text-2xl font-bold text-foreground">{title}</h1>
        {description && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
      </header>
      {children}
    </div>
  );
}

function EmptyState({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-card px-6 py-12 text-center">
      <div className="rounded-full bg-muted p-2 text-muted-foreground">
        {icon}
      </div>
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <p className="max-w-md text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
